import { useEffect } from 'react';
import { requestAvatarAction } from './api';
import type { DashboardAvatarConfig } from './types';

const eventNamespace = '.ipsReactDashboardAvatar';

export const useDashboardAvatar = (avatar: DashboardAvatarConfig, triggerSelector: string): void => {
  useEffect(() => {
    const $ = (window as any).jQuery;
    const templateApi = (window as any)?.wp?.template;

    if (!$) {
      return;
    }

    if (!avatar.enabled || !avatar.ajaxUrl || !avatar.actionNonce) {
      return;
    }

    const templateNode = document.getElementById('tmpl-ywcmap-avatar-modal-content');
    const modal = $('#ips-wcmap-avatar');
    if (!modal.length || !templateNode) {
      return;
    }

    const overlay = modal.find('.avatar-modal-overlay');
    const content = modal.find('.avatar-modal');
    const compiledTemplate = typeof templateApi === 'function' ? templateApi('ywcmap-avatar-modal-content') : null;
    const renderTemplate = (): string => {
      if (typeof compiledTemplate === 'function') {
        try {
          const rendered = compiledTemplate({});
          if (typeof rendered === 'string') {
            return rendered;
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[IPS-React] avatar template rendering failed, fallback to static template', error);
        }
      }

      return templateNode.innerHTML || '';
    };

    let opened = false;
    let requiresCleanup = false;

    const setLoading = (active: boolean) => {
      const blockFn = (content as any)?.block;
      const unblockFn = (content as any)?.unblock;

      if (active) {
        if (typeof blockFn === 'function') {
          blockFn.call(content, {
            message: null,
            overlayCSS: {
              background: '#fff no-repeat center',
              opacity: 0.5,
              cursor: 'none',
            },
          });
        } else {
          content.addClass('ips-react-dashboard-avatar-loading');
        }
        return;
      }

      if (typeof unblockFn === 'function') {
        unblockFn.call(content);
      } else {
        content.removeClass('ips-react-dashboard-avatar-loading');
      }
    };

    const centerModal = () => {
      const windowWidth = $(window).width();
      const windowHeight = $(window).height();
      const margin = windowWidth - 40 > 450 ? `${windowHeight / 8}px` : '0';
      const width = windowWidth - 40 > 450 ? '450px' : 'auto';

      content.css({
        'margin-top': margin,
        'margin-bottom': margin,
        width,
      });
    };

    const clearTempAvatar = async () => {
      if (!requiresCleanup) {
        return;
      }

      try {
        await requestAvatarAction({
          endpoint: avatar.ajaxUrl,
          nonce: avatar.actionNonce,
          action: 'clear_temp_avatar',
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[IPS-React] clear_temp_avatar failed', error);
      } finally {
        requiresCleanup = false;
      }
    };

    const closeModal = async () => {
      if (!opened) {
        return;
      }

      overlay.fadeOut(500);
      content.fadeOut(500, async () => {
        content.find('.avatar-modal-content').remove();
        modal.hide();
        await clearTempAvatar();
        opened = false;
      });
    };

    const openModal = () => {
      if (opened) {
        return;
      }

      opened = true;
      content.append(renderTemplate());
      modal.show();
      overlay.fadeIn(500);
      content.fadeIn(500);
    };

    const resetModalContent = async (event: Event) => {
      event.preventDefault();
      content.find('.avatar-modal-content').replaceWith(renderTemplate()).fadeIn();
      await clearTempAvatar();
    };

    const uploadAvatar = async (event: Event) => {
      event.preventDefault();
      const input = event.currentTarget as HTMLInputElement | null;
      const form = $(input).closest('form');

      const data = new FormData();
      const files = input?.files ? Array.from(input.files) : [];
      files.forEach((file) => {
        data.append(input?.name || 'ywcmap_custom_avatar', file);
      });

      if (!files.length) {
        return;
      }

      setLoading(true);
      try {
        const response = await requestAvatarAction({
          endpoint: avatar.ajaxUrl,
          nonce: avatar.actionNonce,
          action: 'upload_avatar',
          data,
        });

        if (response?.success) {
          requiresCleanup = true;
          form.replaceWith(response.data?.html || '');
          content.find('.avatar-modal-content').addClass('avatar-uploaded');
          return;
        }

        window.location.reload();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[IPS-React] upload_avatar failed', error);
      } finally {
        setLoading(false);
      }
    };

    const setAvatar = async (event: Event) => {
      event.preventDefault();
      setLoading(true);
      try {
        const response = await requestAvatarAction({
          endpoint: avatar.ajaxUrl,
          nonce: avatar.actionNonce,
          action: 'set_avatar',
        });

        if (response?.success !== false) {
          window.location.reload();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[IPS-React] set_avatar failed', error);
      } finally {
        setLoading(false);
      }
    };

    const resetAvatar = async (event: Event) => {
      event.preventDefault();
      setLoading(true);
      try {
        const response = await requestAvatarAction({
          endpoint: avatar.ajaxUrl,
          nonce: avatar.actionNonce,
          action: 'reset_avatar',
        });

        if (response?.success) {
          window.location.reload();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[IPS-React] reset_avatar failed', error);
      } finally {
        setLoading(false);
      }
    };

    centerModal();

    const resolvedTriggerSelector =
      triggerSelector && triggerSelector.trim() !== ''
        ? triggerSelector
        : '.ips-wcmap .user-avatar';

    $(document).on(`click${eventNamespace}`, resolvedTriggerSelector, openModal);
    $(window).on(`resize${eventNamespace}`, centerModal);

    modal.on(`change${eventNamespace}`, '#ywcmap_custom_avatar', uploadAvatar);
    modal.on(`click${eventNamespace}`, '.avatar-modal-close', (event: Event) => {
      event.preventDefault();
      void closeModal();
    });
    modal.on(`click${eventNamespace}`, '.reset-avatar a.cancel', (event: Event) => {
      void resetModalContent(event);
    });
    modal.on(`click${eventNamespace}`, '.reset-avatar a.reset', (event: Event) => {
      void resetAvatar(event);
    });
    modal.on(`click${eventNamespace}`, '.set-avatar button', (event: Event) => {
      void setAvatar(event);
    });

    return () => {
      $(document).off(eventNamespace);
      $(window).off(eventNamespace);
      modal.off(eventNamespace);
    };
  }, [avatar.actionNonce, avatar.ajaxUrl, avatar.enabled, triggerSelector]);
};
